import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { IOTP, IGenerateOTPRequest, IOTPParameters, ITaperingStep, ICoMedicationChecklist, ISaveCoMedicationChecklistRequest, IOTPTimelineEvent } from '../../entities/OTP/OTP';

export interface ICalculateTaperingPreviewRequest {
    originalMME: number;
    parameters: IOTPParameters;
}

export interface ISaveTaperingPlanRequest {
    savedMMECalculationID?: number;
    parameters: IOTPParameters;
    taperingSteps: ITaperingStep[];
}

export interface IAbortOTPRequest {
    reasonCode: number;
    reasonText?: string;
}

export interface ICompleteOTPRequest {
    reasonCode?: number;
    reasonText?: string;
}

export interface IAppendOTPRequest {
    reasonCode: number;
    reasonText?: string;
}

export class OTPApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/OTP`;

    public static apiUrls = {
        generate: `${OTPApi.baseApiUrl}/Generate`,
        getById: `${OTPApi.baseApiUrl}`,
        getByPatientId: `${OTPApi.baseApiUrl}/Patient`,
        getCurrentByPatientId: `${OTPApi.baseApiUrl}/Patient`,
        updateParameters: `${OTPApi.baseApiUrl}`,
        preview: `${OTPApi.baseApiUrl}`,
        calculatePreview: `${OTPApi.baseApiUrl}/CalculatePreview`,
        save: `${OTPApi.baseApiUrl}`,
        delete: `${OTPApi.baseApiUrl}`,
        abort: `${OTPApi.baseApiUrl}`,
        append: `${OTPApi.baseApiUrl}`,
        complete: `${OTPApi.baseApiUrl}`,
        activate: `${OTPApi.baseApiUrl}`,
        submit: `${OTPApi.baseApiUrl}`,
        delegate: `${OTPApi.baseApiUrl}`,
        validate: `${OTPApi.baseApiUrl}`,
        returnToDraft: `${OTPApi.baseApiUrl}`,
        coMedications: `${OTPApi.baseApiUrl}`,
        timeline: `${OTPApi.baseApiUrl}`,
        pdf: `${OTPApi.baseApiUrl}`,
    };

    public static async generateOTP(request: IGenerateOTPRequest): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.generate,
            data: request,
        });
    }

    public static async getOTPById(id: number): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.getById}/${id}`,
        });
    }

    public static async getOTPsByPatientId(patientId: number): Promise<IOTP[]> {
        return this.sendApiRequest<IOTP[]>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.getByPatientId}/${patientId}`,
        });
    }

    public static async getCurrentOTPByPatientId(patientId: number): Promise<IOTP | null> {
        try {
            return await this.sendApiRequest<IOTP>({
                method: HttpMethodEnum.GET,
                path: `${this.apiUrls.getCurrentByPatientId}/${patientId}/current`,
            });
        } catch (error: any) {
            // 404 means no plan exists — return null
            if (error?.httpStatusCode === 404) return null;
            throw error;
        }
    }

    public static async updateParameters(id: number, parameters: IOTPParameters): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.PUT,
            path: `${this.apiUrls.updateParameters}/${id}/parameters`,
            data: parameters,
        });
    }

    public static async previewRecalculation(id: number, parameters: IOTPParameters): Promise<ITaperingStep[]> {
        return this.sendApiRequest<ITaperingStep[]>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.preview}/${id}/preview`,
            data: parameters,
        });
    }

    public static async calculateTaperingPreview(request: ICalculateTaperingPreviewRequest): Promise<ITaperingStep[]> {
        return this.sendApiRequest<ITaperingStep[]>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.calculatePreview,
            data: request,
        });
    }

    public static async saveTaperingPlan(id: number, request: ISaveTaperingPlanRequest): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.PUT,
            path: `${this.apiUrls.save}/${id}`,
            data: request,
        });
    }

    public static async deleteOTP(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: `${this.apiUrls.delete}/${id}`,
        });
    }

    public static async abortOTP(id: number, reasonCode: number, reasonText?: string): Promise<IOTP> {
        const request: IAbortOTPRequest = { reasonCode, reasonText };
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.abort}/${id}/abort`,
            data: request,
        });
    }

    public static async appendOTP(id: number, reasonCode: number, reasonText?: string): Promise<IOTP> {
        const request: IAppendOTPRequest = { reasonCode, reasonText };
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.append}/${id}/append`,
            data: request,
        });
    }

    public static async completeOTP(id: number, reasonCode?: number, reasonText?: string): Promise<IOTP> {
        const request: ICompleteOTPRequest | undefined = reasonCode !== undefined || reasonText !== undefined
            ? { reasonCode, reasonText }
            : undefined;
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.complete}/${id}/complete`,
            data: request,
        });
    }

    public static async activateOTP(id: number, patientAcknowledged: boolean, patientAcknowledgmentNote?: string): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.activate}/${id}/activate`,
            data: { patientAcknowledged, patientAcknowledgmentNote } as any,
        });
    }

    public static async submitOTPForValidation(id: number): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.submit}/${id}/submit`,
        });
    }

    public static async delegateOTPForValidation(id: number, delegatedToHcpId: number): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.delegate}/${id}/delegate`,
            data: { delegatedToHcpID: delegatedToHcpId },
        });
    }

    public static async validateOTPByDelegatedHcp(id: number): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.validate}/${id}/validate`,
        });
    }

    public static async returnOTPToDraft(id: number): Promise<IOTP> {
        return this.sendApiRequest<IOTP>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.returnToDraft}/${id}/return-to-draft`,
        });
    }

    public static async getOTPTimeline(id: number): Promise<IOTPTimelineEvent[]> {
        return this.sendApiRequest<IOTPTimelineEvent[]>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.timeline}/${id}/timeline`,
        });
    }

    public static async getCoMedicationChecklist(id: number): Promise<ICoMedicationChecklist> {
        return this.sendApiRequest<ICoMedicationChecklist>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.coMedications}/${id}/co-medications`,
        });
    }

    public static async saveCoMedicationChecklist(id: number, request: ISaveCoMedicationChecklistRequest): Promise<ICoMedicationChecklist> {
        return this.sendApiRequest<ICoMedicationChecklist>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.coMedications}/${id}/co-medications`,
            data: request,
        });
    }

    public static async downloadOTPPdf(id: number, language: string): Promise<Blob> {
        return this.sendApiRequest<Blob>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.pdf}/${id}/pdf?language=${language}`,
            extraOptionsConfig: { responseType: 'blob' },
        });
    }

    public static downloadPdfFile(pdfBlob: Blob, filename?: string): void {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `OTP_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
